type EmblemProps = {
  className?: string;
  /** Adds an accessible `<title>` (and `role="img"`); omit for a purely
   * decorative use (e.g. next to visible "PureTouch" text), which renders
   * `aria-hidden` instead. */
  title?: string;
  /** `favicon` drops to 2 ridge-arcs and thickens the stroke so the mark
   * still reads at 16–32px; `full` (default) is the four-ring mark used
   * everywhere else. */
  variant?: 'full' | 'favicon';
};

const CX = 24;
const CY = 23;
// Orients each ring's open gap consistently — matches the reference mark.
const RING_ROTATE = 'rotate(58 24 23)';

// Ridge-arc rings, outer fingerprint/ripple detail of "The Touch" mark (see
// docs/redesign/reference-identity-study.html). `pathLength` is each ring's
// true circumference (2πr) so `strokeDasharray` reads as simple lengths
// along it; every ring keeps the same ~76%-visible / ~24%-gap proportion.
const RINGS = [
  { r: 6.5, pathLength: 40.8, dash: '31.0 9.8' },
  { r: 11, pathLength: 69.1, dash: '52.5 16.6' },
  { r: 15.5, pathLength: 97.4, dash: '74.0 23.4' },
  { r: 20, pathLength: 125.7, dash: '95.5 30.2' },
] as const;

// The favicon keeps only the innermost and outermost rings — at 16–32px,
// closely spaced rings (e.g. the middle two) blur together, while these two
// stay legibly separated and still read as concentric.
const FAVICON_RING_RADII: ReadonlySet<number> = new Set([6.5, 20]);

const GLEAM_PATH =
  'M37.5 33.1 Q 38.452 35.548 40.9 36.5 Q 38.452 37.452 37.5 39.9 Q 36.548 37.452 34.1 36.5 Q 36.548 35.548 37.5 33.1 Z';

/**
 * "The Touch" brand emblem — a fingerprint that doubles as water ripples,
 * with its outer ridge resolving into a gleam. Inherits its ink color from
 * `currentColor` (size/color via `className`, e.g. `text-primary-deep`); the
 * gleam is always the warm `--color-gleam` token, never `currentColor`.
 */
export const Emblem = ({ className, title, variant = 'full' }: EmblemProps) => {
  const rings =
    variant === 'favicon' ? RINGS.filter((ring) => FAVICON_RING_RADII.has(ring.r)) : RINGS;
  const strokeWidth = variant === 'favicon' ? 2.6 : 1.7;

  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      <g stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round">
        {rings.map((ring) => (
          <circle
            key={ring.r}
            cx={CX}
            cy={CY}
            r={ring.r}
            pathLength={ring.pathLength}
            strokeDasharray={ring.dash}
            transform={RING_ROTATE}
          />
        ))}
      </g>
      <circle cx={CX} cy={CY} r={2.1} fill="currentColor" stroke="none" />
      <path d={GLEAM_PATH} className="fill-gleam" />
    </svg>
  );
};
